import { useCallback, useEffect, useRef, useState } from "react";
import { WS_URL } from "@/lib/api";
import { getDeviceId } from "@/lib/device";

const ICE = { iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }] };

export function useChat(nick) {
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const sig = useRef({ polite: false, makingOffer: false, ignoreOffer: false });
  const localStream = useRef(new MediaStream());
  const peerIdRef = useRef(null);
  const convRef = useRef(null);
  const mediaRef = useRef({ mic: false, cam: false });

  const [status, setStatus] = useState("connecting");
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState(0);
  const [partnerNick, setPartnerNick] = useState("");
  const [partnerMedia, setPartnerMedia] = useState({ mic: false, cam: false });
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [media, setMedia] = useState({ mic: false, cam: false });
  const [remoteStream, setRemoteStream] = useState(null);
  const [localVersion, setLocalVersion] = useState(0);
  const [ban, setBan] = useState(null);

  const send = useCallback((m) => wsRef.current?.readyState === 1 && wsRef.current.send(JSON.stringify(m)), []);

  const applyTracks = useCallback(() => {
    const pc = pcRef.current;
    if (!pc) return;
    pc.getTransceivers().forEach((t) => {
      const kind = t.receiver.track?.kind;
      const track = localStream.current.getTracks().find((x) => x.kind === kind) || null;
      if (t.sender.track !== track) t.sender.replaceTrack(track).catch(() => {});
    });
  }, []);

  const closePC = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    setRemoteStream(null);
    setPartnerMedia({ mic: false, cam: false });
    setPartnerTyping(false);
  }, []);

  const createPC = useCallback((initiator) => {
    closePC();
    const pc = new RTCPeerConnection(ICE);
    pcRef.current = pc;
    sig.current = { polite: !initiator, makingOffer: false, ignoreOffer: false };
    const rs = new MediaStream();
    setRemoteStream(rs);
    pc.ontrack = (e) => { rs.addTrack(e.track); setRemoteStream(new MediaStream(rs.getTracks())); };
    pc.onicecandidate = (e) => e.candidate && send({ type: "signal", candidate: e.candidate });
    pc.onnegotiationneeded = async () => {
      try {
        sig.current.makingOffer = true;
        await pc.setLocalDescription();
        send({ type: "signal", description: pc.localDescription });
      } catch {} finally { sig.current.makingOffer = false; }
    };
    if (initiator) {
      pc.addTransceiver("audio", { direction: "sendrecv" });
      pc.addTransceiver("video", { direction: "sendrecv" });
      applyTracks();
    }
  }, [applyTracks, closePC, send]);

  const handleSignal = useCallback(async ({ description, candidate }) => {
    const pc = pcRef.current;
    if (!pc) return;
    const s = sig.current;
    try {
      if (description) {
        const collision = description.type === "offer" && (s.makingOffer || pc.signalingState !== "stable");
        s.ignoreOffer = !s.polite && collision;
        if (s.ignoreOffer) return;
        await pc.setRemoteDescription(description);
        if (description.type === "offer") {
          pc.getTransceivers().forEach((t) => { t.direction = "sendrecv"; });
          applyTracks();
          await pc.setLocalDescription();
          send({ type: "signal", description: pc.localDescription });
        }
      } else if (candidate) {
        try { await pc.addIceCandidate(candidate); } catch (e) { if (!s.ignoreOffer) console.warn(e); }
      }
    } catch (e) { console.warn("signal error", e); }
  }, [applyTracks, send]);

  useEffect(() => {
    const ws = new WebSocket(`${WS_URL}?nick=${encodeURIComponent(nick || "Ismeretlen")}&device=${encodeURIComponent(getDeviceId())}`);
    wsRef.current = ws;
    let typingTimer;
    ws.onopen = () => ws.send(JSON.stringify({ type: "find" }));
    ws.onclose = () => setStatus((s) => (s === "terminated" || s === "banned" ? s : "disconnected"));
    ws.onmessage = (ev) => {
      const m = JSON.parse(ev.data);
      switch (m.type) {
        case "hello": peerIdRef.current = m.peer_id; break;
        case "online": setOnline(m.count); break;
        case "waiting": setStatus("waiting"); break;
        case "matched":
          convRef.current = m.conversation_id;
          setMessages([]);
          setPartnerNick(m.partner_nick);
          setStatus("chatting");
          createPC(m.initiator);
          send({ type: "media", ...mediaRef.current });
          break;
        case "chat": setMessages((p) => [...p, m]); setPartnerTyping(false); break;
        case "signal": handleSignal(m); break;
        case "media": setPartnerMedia({ mic: !!m.mic, cam: !!m.cam }); break;
        case "typing":
          setPartnerTyping(true);
          clearTimeout(typingTimer);
          typingTimer = setTimeout(() => setPartnerTyping(false), 2500);
          break;
        case "partner_left": convRef.current = null; closePC(); setStatus("left"); break;
        case "terminated": convRef.current = null; closePC(); setStatus("terminated"); break;
        case "banned": convRef.current = null; closePC(); setBan(m); setStatus("banned"); break;
        default: break;
      }
    };
    const stream = localStream.current;
    return () => {
      ws.close();
      pcRef.current?.close();
      stream.getTracks().forEach((t) => t.stop());
    };
  }, [nick, createPC, handleSignal, closePC, send]);

  const toggle = useCallback(async (key) => {
    const kind = key === "mic" ? "audio" : "video";
    const on = !mediaRef.current[key];
    const stream = localStream.current;
    stream.getTracks().filter((t) => t.kind === kind).forEach((t) => { t.stop(); stream.removeTrack(t); });
    if (on) {
      const got = await navigator.mediaDevices.getUserMedia(kind === "audio" ? { audio: true } : { video: { width: 640, height: 480 } });
      got.getTracks().forEach((t) => stream.addTrack(t));
    }
    mediaRef.current = { ...mediaRef.current, [key]: on };
    setMedia(mediaRef.current);
    setLocalVersion((v) => v + 1);
    applyTracks();
    send({ type: "media", ...mediaRef.current });
  }, [applyTracks, send]);

  const next = useCallback(() => { convRef.current = null; closePC(); setMessages([]); setStatus("waiting"); send({ type: "next" }); }, [closePC, send]);
  const stop = useCallback(() => { convRef.current = null; closePC(); setStatus("stopped"); send({ type: "stop" }); }, [closePC, send]);
  const find = useCallback(() => { setMessages([]); setStatus("waiting"); send({ type: "find" }); }, [send]);
  const sendText = useCallback((text) => send({ type: "chat", text }), [send]);
  const sendAttachment = useCallback((file_id) => send({ type: "attachment", file_id }), [send]);
  const typing = useCallback(() => send({ type: "typing" }), [send]);

  return {
    status, messages, online, partnerNick, partnerMedia, partnerTyping, media, remoteStream, ban,
    localStream: localStream.current, localVersion, peerIdRef, convRef,
    toggle, next, stop, find, sendText, sendAttachment, typing,
  };
}
